import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import AbsenceReport from "@/components/AbsenceReport";
import OutOfPolicyJustified from "@/components/OutOfPolicyJustified";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  CalendarX2, CheckCircle2, Clock4, XCircle, AlertTriangle, Loader2, Eye, ThumbsUp, ThumbsDown, Trash2,
} from "lucide-react";
import { formatDMY, crTime, toISODate } from "@/lib/dateFormat";

function StatusBadge({ status, origin, outOfPolicy }) {
  const map = {
    auto_justified: ["bg-emerald-100 text-emerald-800 border-emerald-200", <CheckCircle2 key="i" className="h-3 w-3" />, "Auto-just."],
    approved: ["bg-emerald-100 text-emerald-800 border-emerald-200", <CheckCircle2 key="i" className="h-3 w-3" />, "Aprobada"],
    pending_review: ["bg-amber-100 text-amber-800 border-amber-200", <Clock4 key="i" className="h-3 w-3" />, "Pendiente"],
    needs_review: ["bg-amber-100 text-amber-800 border-amber-200", <Clock4 key="i" className="h-3 w-3" />, "Necesita revisión"],
    mixed: ["bg-blue-100 text-blue-800 border-blue-200", <AlertTriangle key="i" className="h-3 w-3" />, "Mixto"],
    rejected: ["bg-red-100 text-red-800 border-red-200", <XCircle key="i" className="h-3 w-3" />, "Rechazada"],
    withdrawn: ["bg-slate-100 text-slate-700 border-slate-200", <XCircle key="i" className="h-3 w-3" />, "Retirada"],
  };
  // "Auto-just." es SOLO del estudiante. Si la marcó la profe o la
  // administración desde la hoja, se dice quién fue: el estado interno es el
  // mismo y antes las tres se veían igual (caso 29/09/2026).
  let m = map[status] || map.pending_review;
  const justificada = status === "auto_justified" || status === "approved";
  if (justificada && origin === "teacher") {
    m = ["bg-sky-100 text-sky-800 border-sky-200", <CheckCircle2 key="i" className="h-3 w-3" />, "Justif. por la profe"];
  } else if (justificada && origin === "admin") {
    m = ["bg-indigo-100 text-indigo-800 border-indigo-200", <CheckCircle2 key="i" className="h-3 w-3" />, "Justif. por dirección"];
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge variant="outline" className={`${m[0]} border gap-1 inline-flex`}>
        {m[1]}{m[2]}
      </Badge>
      {outOfPolicy && (
        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 border gap-1 inline-flex"
               title="Se avisó con menos horas de las que pide la política">
          <AlertTriangle className="h-3 w-3" /> Fuera de política
        </Badge>
      )}
    </span>
  );
}

function OverridesTable({ rows, onDecide }) {
  const STATUS_ES = {
    present: "presente", absent: "ausente", unjustified: "injustificada",
    unjustified_consumed: "injustificada", teacher_cancelled: "cancelada por la profe",
    not_counted: "no contabiliza", removed: "eliminada del plan", justified: "justificada",
  };
  if (!rows.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500"
           data-testid="overrides-empty">
        <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
        No hay cambios esperando validación.
        <div className="text-xs text-slate-400 mt-1">
          Acá aparecen los casos en que una profe quiere marcar de otra forma una clase
          que el estudiante ya había cancelado a tiempo.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="overrides-table">
      {rows.map((r) => (
        <div key={r.id} className="bg-white border border-amber-200 rounded-xl p-4"
             data-testid={`override-row-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">
                {r.student_name} <span className="text-slate-400 font-normal">·</span>{" "}
                <span className="text-slate-600 font-normal">{r.group_name}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Clase del {formatDMY(r.date)}
              </div>
              <div className="text-[13px] text-slate-700 mt-2">
                <strong>{r.requested_by_name || "La profe"}</strong> quiere marcarla como{" "}
                <strong>{STATUS_ES[r.to_status] || r.to_status}</strong>.
              </div>
              <div className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200
                              rounded-lg px-2.5 py-1.5 mt-2 inline-block">
                El estudiante canceló el{" "}
                {formatDMY((r.cancellation?.submitted_at_cr || "").slice(0, 10))} a las{" "}
                {(r.cancellation?.submitted_at_cr || "").slice(11, 16)}
                {r.cancellation?.hours_notice != null &&
                  ` · ${Math.round(r.cancellation.hours_notice)} h de anticipación`}
              </div>
              {r.notes && (
                <div className="text-[12px] text-slate-500 mt-2">Nota de la profe: {r.notes}</div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm"
                      onClick={() => onDecide(r.id, "reject")}
                      data-testid={`override-reject-${r.id}`}>
                <ThumbsDown className="h-3.5 w-3.5" /> Dejar justificada
              </Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => onDecide(r.id, "approve")}
                      data-testid={`override-approve-${r.id}`}>
                <ThumbsUp className="h-3.5 w-3.5" /> Aplicar el cambio
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AbsenceRequestsAdmin() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canHardDelete = ["superadmin", "academic_director"].includes(user?.role);
  const [tab, setTab] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState({});
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  // Cambios de asistencia que piden las profes sobre clases que el alumno
  // canceló a tiempo: no se aplican hasta que la administración los valide.
  const [overrides, setOverrides] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, u, g, o] = await Promise.all([
        api.get("/absence-requests"),
        api.get("/users"),
        api.get("/groups"),
        api.get("/attendance-overrides", { params: { status: "pending" } }),
      ]);
      setRequests(r.data || []);
      setUsers(Object.fromEntries((u.data || []).map((x) => [x.user_id, x])));
      setGroups(Object.fromEntries((g.data || []).map((x) => [x.id, x])));
      setOverrides(o.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decideOverride = async (id, decision) => {
    try {
      await api.post(`/attendance-overrides/${id}/${decision}`, {});
      toast.success(decision === "approve"
        ? "Cambio aplicado en la hoja de asistencia"
        : "Cambio rechazado: la justificación del estudiante queda como estaba");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const hardDelete = async (r) => {
    try {
      const res = await api.delete(`/absence-requests/${r.id}/hard`);
      toast.success(`Solicitud eliminada${res.data?.attendance_rows_removed ? ` (${res.data.attendance_rows_removed} asistencia(s) revertidas)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const pending = useMemo(
    () => requests.filter((r) => r.status_summary === "needs_review" || r.status_summary === "mixed"),
    [requests],
  );
  const visible = tab === "pending" ? pending : requests;

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="absence-requests-admin">
      <PageHeader
        title={t("absence_requests_title")}
        subtitle={t("absence_requests_subtitle")}
        testId="absence-admin-header"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" data-testid="tab-pending">
            {t("pending")} ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            {t("all")} ({requests.length})
          </TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">
            Reporte
          </TabsTrigger>
          <TabsTrigger value="overrides" data-testid="tab-overrides">
            Cambios por validar{overrides.length ? ` (${overrides.length})` : ""}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {tab === "report" ? (
            <div className="space-y-8">
              <AbsenceReport />
              <OutOfPolicyJustified />
            </div>
          ) : tab === "overrides" ? (
            <OverridesTable rows={overrides} onDecide={decideOverride} />
          ) : (
            <RequestsTable
              rows={visible}
              users={users}
              groups={groups}
              onReview={(r) => setReviewing(r)}
              onDelete={canHardDelete ? hardDelete : null}
            />
          )}
        </TabsContent>
      </Tabs>

      {reviewing && (
        <ReviewDialog
          request={reviewing}
          student={users[reviewing.student_id]}
          group={groups[reviewing.group_id]}
          onClose={() => setReviewing(null)}
          onReviewed={() => { setReviewing(null); load(); }}
        />
      )}
    </div>
  );
}

function RequestsTable({ rows, users, groups, onReview, onDelete }) {
  const { t } = useI18n();
  if (rows.length === 0) {
    return (
      <div className="p-10 text-center text-slate-500 bg-white border border-slate-200 rounded-xl" data-testid="no-pending-requests">
        {t("no_pending_requests")}
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("submitted")}</TableHead>
            <TableHead>{t("student")}</TableHead>
            <TableHead>{t("group")}</TableHead>
            <TableHead>{t("dates")}</TableHead>
            <TableHead>{t("status")}</TableHead>
            <TableHead className="text-right">{t("review")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const student = users[r.student_id];
            const group = groups[r.group_id];
            return (
              <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap"
                           data-testid={`request-submitted-${r.id}`}>
                  {/* Fecha Y HORA del envío, siempre en hora de Costa Rica. */}
                  <div className="text-slate-700 font-medium">{formatDMY(toISODate(new Date(r.submitted_at)))}</div>
                  <div className="text-[11px] text-slate-500">{crTime(r.submitted_at)}</div>
                </TableCell>
                <TableCell className="font-medium">{student?.name || r.student_id}</TableCell>
                <TableCell>{group?.name || t("group_deleted")}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(r.dates || []).map((d, i) => (
                      <Badge key={i} variant="outline" className="text-[11px]">
                        {d.date}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={r.status_summary}
                    origin={r.origin}
                    outOfPolicy={(r.dates || []).some((d) => d.within_policy === false)}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onReview(r)}
                      data-testid={`review-btn-${r.id}`}
                      title="Ver / Revisar"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {onDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-btn-${r.id}`}
                            title="Eliminar (limpieza)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid={`delete-dialog-${r.id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar esta solicitud?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción es permanente. Se eliminará la solicitud del estudiante
                              <strong> {student?.name || r.student_id}</strong> y también se
                              revertirán las asistencias asociadas (si esta solicitud consumió
                              una clase, se liberará el conteo).
                              <br /><br />
                              Úsalo solo para limpiar registros de prueba.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`delete-cancel-${r.id}`}>
                              Cancelar
                            </AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => onDelete(r)}
                              data-testid={`delete-confirm-${r.id}`}
                            >
                              Eliminar definitivamente
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ReviewDialog({ request, student, group, onClose, onReviewed }) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const pendingDates = (request.dates || []).filter((d) => d.status === "pending_review");

  const review = async (action) => {
    setBusy(true);
    try {
      await api.post(`/absence-requests/${request.id}/review`, {
        action,
        admin_note: note || null,
      });
      toast.success(action === "approve" ? t("request_approved") : t("request_rejected"));
      onReviewed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>{t("review_request_title")}</DialogTitle>
          <DialogDescription>
            {student?.name || request.student_id} · {group?.name || request.group_id}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm" data-testid="review-submitted-at">
            <span className="text-slate-500">Enviada: </span>
            <span className="text-slate-900">
              {formatDMY(toISODate(new Date(request.submitted_at)))} · {crTime(request.submitted_at)}
            </span>
            <span className="text-[11px] text-slate-400 ml-1">(hora de Costa Rica)</span>
            {request.origin === "teacher" && (
              <div className="text-[12px] text-sky-800 mt-1">
                La marcó <strong>{request.created_by_name || "la profesora"}</strong> desde la hoja
                de asistencia (no la pidió el estudiante).
              </div>
            )}
            {request.origin === "admin" && (
              <div className="text-[12px] text-indigo-800 mt-1">
                La marcó <strong>{request.created_by_name || "la administración"}</strong> desde la
                hoja de asistencia (no la pidió el estudiante).
              </div>
            )}
          </div>
          <div className="text-sm">
            <span className="text-slate-500">{t("reason")}: </span>
            <span className="text-slate-900">{request.reason || "—"}</span>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">{t("dates_in_request")}</Label>
            {(request.dates || []).map((d, i) => (
              <div
                key={i}
                className={`p-2.5 rounded-lg border text-sm ${
                  d.status === "pending_review"
                    ? "bg-amber-50 border-amber-200"
                    : d.status === "auto_justified" || d.status === "approved"
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-slate-50 border-slate-200"
                }`}
                data-testid={`review-date-${d.date}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <strong>{d.date}</strong>
                  <StatusBadge status={d.status} origin={request.origin} outOfPolicy={d.within_policy === false} />
                </div>
                <div className="mt-1 text-[11px] text-slate-600 flex gap-3 flex-wrap">
                  {d.hours_notice !== null && (
                    <span>
                      {t("hours_notice_label")}: <strong>{d.hours_notice}h</strong>
                      {d.policy_hours ? ` / ${d.policy_hours}h` : ""}
                    </span>
                  )}
                  {!d.within_policy && (
                    <span className="text-amber-700">⚠️ {t("out_of_policy")}</span>
                  )}
                  {d.exceeds_quota && (
                    <span className="text-amber-700">⚠️ {t("exceeds_quota_short")}</span>
                  )}
                </div>
                {d.admin_note && (
                  <div className="mt-1 text-[11px] text-blue-700 italic">&ldquo;{d.admin_note}&rdquo;</div>
                )}
              </div>
            ))}
          </div>

          {pendingDates.length > 0 && (
            <div>
              <Label className="text-xs">{t("admin_note_optional")}</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder={t("admin_note_placeholder")}
                data-testid="admin-note"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("close")}</Button>
          {pendingDates.length > 0 && (
            <>
              <Button
                variant="destructive"
                onClick={() => review("reject")}
                disabled={busy}
                data-testid="reject-btn"
              >
                <ThumbsDown className="h-4 w-4 mr-1" /> {t("reject")}
              </Button>
              <Button
                onClick={() => review("approve")}
                disabled={busy}
                className="btn-brand"
                data-testid="approve-btn"
              >
                <ThumbsUp className="h-4 w-4 mr-1" /> {t("approve")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
